import { useState, useEffect } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../../../components/ui_components/FounderWrapper';
import colors from '../../../../styles/colors';
import { apiGetPacks, apiDeletePack } from '../../../../api/packApi';
import type { Pack } from '../../../../types/models/pack';
import PackCreateModal from './PackCreateModal';

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  padding: 32px 24px;
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: gold;
  margin: 0;
  flex: 1;
`;

const NewBtn = styled.button`
  padding: 8px 16px;
  background-color: ${colors.bgColorBordeaux};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background-color: ${colors.bgColorRed}; }
`;

const StatusText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  margin-top: 40px;
`;

const ErrorText = styled.p`
  color: #dc3545;
  text-align: center;
  margin-top: 40px;
`;

const PackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  margin-bottom: 10px;
`;

const ExpThumb = styled.img`
  width: 48px;
  height: 68px;
  object-fit: cover;
  border-radius: 3px;
  flex-shrink: 0;
  background-color: #2a2a2a;
`;

const ExpThumbPlaceholder = styled.div`
  width: 48px;
  height: 68px;
  border-radius: 3px;
  background-color: #2a2a2a;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  color: rgba(255, 255, 255, 0.2);
  text-align: center;
`;

const PackName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PackMeta = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
`;

const ActiveBadge = styled.span<{ active: boolean }>`
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${({ active }) => (active ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)')};
  color: ${({ active }) => (active ? '#22c55e' : 'rgba(255,255,255,0.35)')};
  border: 1px solid ${({ active }) => (active ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)')};
  flex-shrink: 0;
`;

const EditBtn = styled.button`
  padding: 6px 12px;
  background-color: transparent;
  color: gold;
  border: 1px solid gold;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background-color: rgba(255, 215, 0, 0.12); }
`;

const DeleteBtn = styled.button`
  padding: 6px 12px;
  background-color: transparent;
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.5);
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background-color: rgba(220, 53, 69, 0.1); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const PackListPage: React.FC = () => {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPack, setEditingPack] = useState<Pack | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

  useEffect(() => {
    let isMounted = true;
    apiGetPacks()
      .then((res) => { if (isMounted) setPacks(res.data); })
      .catch(() => { if (isMounted) setError('Errore durante il caricamento dei pacchetti.'); })
      .finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPack(null);
  };

  const handleDelete = async (pack: Pack) => {
    if (!window.confirm(`Eliminare il pacchetto "${pack.name}"? L'operazione è irreversibile.`)) return;
    setDeletingId(pack.id);
    try {
      await apiDeletePack(pack.id);
      setPacks((prev) => prev.filter((p) => p.id !== pack.id));
    } catch (err: any) {
      alert(err?.message ?? 'Eliminazione fallita.');
    } finally {
      setDeletingId(null);
    }
  };

  const toUrl = (path: string | null | undefined) =>
    path ? (path.startsWith('http') ? path : storageUrl + path) : null;

  return (
    <>
      <CommonWrapper>
        <PageInner>
          <PageHeader>
            <PageTitle>Gestisci Pacchetti</PageTitle>
            <NewBtn onClick={() => setIsModalOpen(true)}>+ Nuovo Pacchetto</NewBtn>
          </PageHeader>

          {isLoading && <StatusText>Caricamento...</StatusText>}
          {error && <ErrorText>{error}</ErrorText>}
          {!isLoading && !error && packs.length === 0 && (
            <StatusText>Nessun pacchetto creato.</StatusText>
          )}

          {packs.map((pack) => {
            const imgSrc = toUrl(pack.expansion.expansion_path);
            return (
              <PackRow key={pack.id}>
                {imgSrc ? (
                  <ExpThumb src={imgSrc} alt={pack.expansion.name} />
                ) : (
                  <ExpThumbPlaceholder>No img</ExpThumbPlaceholder>
                )}
                <PackName>{pack.name}</PackName>
                <PackMeta>{pack.expansion.name}</PackMeta>
                <PackMeta>{pack.cost} crediti</PackMeta>
                <PackMeta>{pack.cards_per_pack} carte</PackMeta>
                <ActiveBadge active={pack.is_active}>
                  {pack.is_active ? 'Attivo' : 'Non attivo'}
                </ActiveBadge>
                <EditBtn onClick={() => { setEditingPack(pack); setIsModalOpen(true); }}>
                  Modifica
                </EditBtn>
                <DeleteBtn
                  onClick={() => handleDelete(pack)}
                  disabled={deletingId === pack.id}
                >
                  {deletingId === pack.id ? '...' : 'Elimina'}
                </DeleteBtn>
              </PackRow>
            );
          })}
        </PageInner>
      </CommonWrapper>

      {isModalOpen && (
        <PackCreateModal
          pack={editingPack ?? undefined}
          onClose={handleCloseModal}
          onCreated={() => {
            apiGetPacks().then((res) => setPacks(res.data)).catch(() => {});
            handleCloseModal();
          }}
          onUpdated={() => {
            apiGetPacks().then((res) => setPacks(res.data)).catch(() => {});
            handleCloseModal();
          }}
        />
      )}
    </>
  );
};

export default PackListPage;
